import { Box, Grid, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IJob,
  ITeam,
  shareJobWithTeam,
  IShareJobWithTeam,
  getAllTeamsInWorkspace,
} from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDataTable,
  WsSelect,
  WsSelectObj,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
} from 'wisestep-base';

import { success } from '../../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';

export const StyledFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: '10px',
  textAlign: 'center',
  right: '5px',
  justifyContent: 'flex-end',
  width: '29vw',
  backgroundColor: WsColors.White,
}));

interface EditJobAdminTeamProps {
  job: IJob;
  closeDrawer(): void;
  onJobAdminTeamChange(): void;
}

export const defaultValues: IShareJobWithTeam = {
  jobId: '',
  team: {} as ITeam,
  type: '',
  mappingCount: 0,
  submissionCount: 0,
};
const EditJobAdminTeam = ({ job, closeDrawer, onJobAdminTeamChange }: EditJobAdminTeamProps) => {
  const methods = useForm();
  const { watch } = methods;
  const [userTeams, setUserTeams] = useState<ITeam[]>();
  const [selectedTeam, setSelectedTeam] = useState<ITeam>();
  defaultValues.mappingCount = job.mappings ?? 0;
  defaultValues.submissionCount = job.submissions ?? 0;
  defaultValues.jobId = job.id ?? '';
  const setNotification = useNotificationStore((state) => state.setNotification);

  const columns = [
    { Header: 'Teams', accessor: 'teamName' },
    { Header: 'Action', accessor: 'action' },
  ];
  const updateOption: string = watch('updateOption');

  const selectTeam = (team: ITeam) => setSelectedTeam(team);

  const options: WsSelectObj[] = [
    {
      label: 'Update Candidate Mapping(s) Only',
      value: 'mapping',
    },
    {
      label: 'Update Candidate Submission(s) Only',
      value: 'submission',
    },
    {
      label: 'Update Candidate Submission(s) and Mapping(s)',
      value: 'all',
    },
    {
      label: "Don't Update Prior Candidate Mapping(s) and Submission(s)",
      value: 'dontupdate',
    },
  ];

  const assignTeam = async () => {
    if (job.id && selectedTeam) {
      defaultValues.team = selectedTeam;
      await shareJobWithTeam(defaultValues);
      setNotification(success('Job Admin Team has been updated'));
      closeDrawer();
      onJobAdminTeamChange();
    }
  };

  const tableData: Record<string, unknown>[] = userTeams
    ? userTeams.map((element) => ({
        teamName: element.name ?? '',
        action: (
          <WsButton
            variant={selectedTeam == element ? 'contained' : 'outlined'}
            size="small"
            onClick={() => selectTeam(element)}
          >
            Select
          </WsButton>
        ),
      }))
    : [];

  useEffect(() => {
    const teams = async () => {
      const userTeamsResponse = await getAllTeamsInWorkspace();
      setUserTeams(userTeamsResponse?.data);
    };

    teams();
  }, []);

  return (
    <Grid p={2}>
      <WsDataTable
        preSelectedRows={[]}
        columns={columns}
        data={tableData}
        boldItems={['teamName']}
      />
      <StyledFooter>
        {selectedTeam != null && (
          <Grid pt={3} pr={2} pb={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Update prior submissions and mappings
            </WsTypography>
            <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.QUATERNARY}>
              {`Prior ${defaultValues.mappingCount} Mapping(s) and ${defaultValues.submissionCount} Submission(s) are associated with ${job?.team?.name}. Select an option from
              below to update the new team name&apos;s right.`}
            </WsTypography>
            <Box pt={2}>
              <FormProvider {...methods}>
                <WsSelect
                  name="updateOption"
                  size="small"
                  label="Update Prior Mappings"
                  selectArray={options}
                />
              </FormProvider>
            </Box>
          </Grid>
        )}
        <Grid container item justifyContent="flex-end" pr={1} py={1}>
          <WsButton
            variant="contained"
            size="large"
            disabled={selectedTeam == null && updateOption != ''}
            onClick={assignTeam}
          >
            Update
          </WsButton>
        </Grid>
      </StyledFooter>
    </Grid>
  );
};

export default EditJobAdminTeam;
